import Route from '@ember/routing/route';
import { service } from '@ember/service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type PslfProfileModel from 'tio-data/models/pslf-profile';
import type EmployeeModel from 'tio-data/models/employee';
import type CompanyModel from 'tio-data/models/company';

export default class AuthenticatedPslfRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  beforeModel() {
    // PSLF is an employee-scoped flow: child routes and templates
    // (dashboard, start-new, sign, etc.) all dereference model.pslfProfile
    // and model.pslfProfile.employee. If we have no current employee there
    // is no pslf profile to load — redirect back to the authenticated root
    // instead of returning a null model that crashes further down the tree.
    if (!this.sessionContext.currentEmployee?.id) {
      this.router.replaceWith('authenticated');
    }
  }

  async model() {
    const currentEmployee = this.sessionContext.currentEmployee;
    // beforeModel above guarantees currentEmployee?.id is truthy.
    const employee = await this.store.findRecord<EmployeeModel>('employee', currentEmployee!.id!, {
      include: 'pslf-profile,pslf-forms,employee-addition',
      reload: true,
    });
    const companyId = employee?.company?.id as string;
    // Start company fetch in parallel with profile create (if needed)
    const companyPromise = this.store.findRecord<CompanyModel>('company', companyId, {
      include: 'pslf-groups',
      reload: true,
    });
    let profile = employee.pslfProfile;
    if (!profile) {
      profile = this.store.createRecord<PslfProfileModel>('pslf-profile', {
        employee,
      });
      await profile.save();
    }
    const company = await companyPromise;
    return {
      pslfProfile: profile,
      groups: company.pslfGroups,
    };
  }
}
